import { isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    FileSearchOutlined,
    SearchOutlined,
    UnorderedListOutlined,
    UserOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import { Empty, Select, Spin, Tabs } from "antd";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";

import { RVIcon, RVIconSizeOptions, RVColumn, RVModal } from "@regal-voice/ui";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { routerBaseName } from "App/routes/navigation";

import { useGetSearchAiAgents } from "./Components/AiAgents/useGetSearchAiAgents";
import { useGetSearchAudiences } from "./Components/Audience/useGetSearchAudiences";
import { useGetSearchCampaigns } from "./Components/Campaigns/useGetSearchCampaigns";
import { useCommands } from "./Components/Commands/useCommands";
import { useGetCallCustomers } from "./Components/InAppAction/useGetCallCustomers";
import { useGetSearchJourneys } from "./Components/Journeys/useGetSearchJourneys";
import { useGetRoutes } from "./Components/Routing/useGetRoutes";
import { useGetSearchUsers } from "./Components/Users/useGetSearchUsers";
import { getDefaultResults } from "./defaultResults";
import { useSpotlightSearch } from "./SpotlightSearchContext";
import { SpotlightItem, SpotlightOption } from "./types";
import { getRelevanceScore } from "./utils";

import styles from "./SpotlightSearch.module.scss";

const DEBOUNCE_DELAY = 300;

export function SpotlightSearch(): JSX.Element {
    const navigate = useNavigate();
    const {
        audiencePages,
        aiAgentsPages,
        orchestrationPages,
        showSettings,
        settingsPages,
        showAIAgents,
        showReporting,
        showADToAgent,
        showADToAdmin,
    } = usePageAvailability();
    const [searchValue, setSearchValue] = useState<string>("");
    const [options, setOptions] = useState<SpotlightOption[]>([]);
    const { isOpen, openSpotlight, closeSpotlight } = useSpotlightSearch();
    const onCancel = useCallback(() => {
        closeSpotlight();
    }, [closeSpotlight]);
    const handleCloseModal = useCallback(() => {
        onCancel();
        setIsLoading(false);
        setDocsLoading(false);
        setOptions([]);
        setSearchValue("");
        itemRefs.current = [];
    }, [onCancel]);
    const { getUsers, loading: usersLoading } = useGetSearchUsers(handleCloseModal);
    const { getRoutes } = useGetRoutes(handleCloseModal);
    const { getJourneys, loading: journeysLoading } = useGetSearchJourneys(handleCloseModal);
    const { getCampaigns, loading: campaignsLoading } = useGetSearchCampaigns(handleCloseModal);
    const { getAiAgents, loading: aiAgentsLoading } = useGetSearchAiAgents(handleCloseModal);
    const { getAudiences, loading: audiencesLoading } = useGetSearchAudiences({ onClose: handleCloseModal });
    const [docsLoading, setDocsLoading] = useState(false);
    const { getCallCustomers, loading: callCustomersLoading } = useGetCallCustomers(handleCloseModal);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [selectedItemIndex, setSelectedItemIndex] = useState(0);

    const selectRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
    const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(true);
    const { commands } = useCommands();

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.metaKey && event.key === "k") {
                openSpotlight();
            }
        };
        window.addEventListener("keydown", handleKeyPress);
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    }, [openSpotlight]);

    const sortAndCombineResults = useCallback((results: SpotlightOption[], searchTerm: string) => {
        if (!searchTerm) {
            return results;
        }

        const sortedResults = results
            .map((option) => ({
                ...option,
                options: option.options
                    .map((opt) => ({
                        ...opt,
                        score: getRelevanceScore(opt, searchTerm),
                    }))
                    .sort((a, b) => b.score - a.score),
            }))
            .filter((option) => option.options.length > 0);

        // Move docs to the end
        const docsIndex = sortedResults.findIndex((option) => option.label === "Regal Docs");
        if (docsIndex !== -1) {
            const docs = sortedResults.splice(docsIndex, 1);
            sortedResults.push(...docs);
            // Reset selection to first item when docs are moved
            setSelectedItemIndex(0);
        }

        return sortedResults;
    }, []);

    const debouncedSearch = useMemo(
        () =>
            debounce(async (value: string) => {
                setSearchValue(value);
                setIsKeyboardNavigation(true);
                if (!value.trim().length) {
                    // Show default results when search is empty
                    setOptions(
                        getDefaultResults(navigate, handleCloseModal, {
                            showAgentDesktop: showADToAgent || showADToAdmin,
                            showReporting,
                            showSettings,
                            showAIAgents,
                            showCustomAgents: aiAgentsPages.showCustomAgents,
                            showKnowledgeBase: aiAgentsPages.showKnowledgeBase,
                            showContacts: audiencePages.showContacts,
                            showCampaigns: orchestrationPages.showCampaigns,
                            showJourneys: orchestrationPages.showJourneys,
                            showUsers: showSettings && settingsPages.showUserManagement,
                        })
                    );
                    setDocsLoading(false);
                    return;
                }
                setSearchValue(value);

                // Reset selection and scroll position on new search
                setSelectedItemIndex(0);
                setSelectedTabIndex(0);
                requestAnimationFrame(() => {
                    const container = containerRef.current;
                    if (container) {
                        container.scrollTop = 0;
                    }
                });

                if (value.startsWith("/") && commands.length > 0) {
                    if (value.split(" ").length > 1 && value.split(" ")[0] === "/call") {
                        try {
                            const callCustomers = await getCallCustomers(value.split(" ")[1], "call");
                            setOptions(sortAndCombineResults(callCustomers, value));
                            setSelectedItemIndex(0);
                        } catch {}
                        return;
                    }

                    // Show commands
                    const filteredOptions = [...commands].filter((option) =>
                        option.action.startsWith(value.split(" ")[0])
                    );
                    if (filteredOptions.length) {
                        setOptions([
                            {
                                label: "Commands",
                                options: filteredOptions.map((option: any) => ({
                                    disabled: true,
                                    label: option.label,
                                    value: option.action,
                                })),
                            },
                        ]);
                        setSelectedItemIndex(0);
                    } else {
                        setOptions([]);
                    }
                    return;
                }

                setOptions([]);
                setSelectedItemIndex(0);

                // Start all search operations independently
                (async () => {
                    try {
                        const routes = getRoutes(value);
                        if (routes.length > 0) {
                            setOptions((prev) => sortAndCombineResults([...prev, ...routes], value));
                        }
                    } catch {}
                })();

                // Search users if user has access to Settings and User Management
                if (showSettings && settingsPages.showUserManagement) {
                    (async () => {
                        try {
                            const users = await getUsers(value);
                            if (users && users.length > 0) {
                                setOptions((prev) => sortAndCombineResults([...prev, ...users], value));
                            }
                        } catch {}
                    })();
                }

                // Search journeys if user has access
                if (orchestrationPages.showJourneys) {
                    (async () => {
                        try {
                            const journeys = await getJourneys(value);
                            if (journeys && journeys.length > 0) {
                                setOptions((prev) => sortAndCombineResults([...prev, ...journeys], value));
                            }
                        } catch {}
                    })();
                }

                // Search campaigns if user has access
                if (orchestrationPages.showCampaigns) {
                    (async () => {
                        try {
                            const campaigns = await getCampaigns(value);
                            if (campaigns && campaigns.length > 0) {
                                setOptions((prev) => sortAndCombineResults([...prev, ...campaigns], value));
                            }
                        } catch {}
                    })();
                }

                // Search AI agents if user has access
                if (showAIAgents) {
                    (async () => {
                        try {
                            const aiAgents = await getAiAgents(value);
                            if (aiAgents && aiAgents.length > 0) {
                                setOptions((prev) => sortAndCombineResults([...prev, ...aiAgents], value));
                            }
                        } catch {}
                    })();
                }

                // Docs preview disabled - backend endpoint not implemented
                // (async () => {
                //     try {
                //         const docsPreview = await getDocsPreview({ query: value, setIsLoading: setDocsLoading });
                //         if (docsPreview && docsPreview.length > 0) {
                //             setOptions((prev) => sortAndCombineResults([...prev, ...docsPreview], value));
                //         }
                //     } catch {}
                // })();

                // Search audiences if user has access
                if (audiencePages.showContacts) {
                    (async () => {
                        try {
                            const audiences = await getAudiences(value);
                            if (audiences && audiences.length > 0) {
                                setOptions((prev) => sortAndCombineResults([...prev, ...audiences], value));
                            }
                        } catch {}
                    })();
                }
            }, DEBOUNCE_DELAY),
        [
            sortAndCombineResults,
            aiAgentsPages.showCustomAgents,
            aiAgentsPages.showKnowledgeBase,
            getAudiences,
            navigate,
            handleCloseModal,
            commands,
            getRoutes,
            getUsers,
            getJourneys,
            getCampaigns,
            getAiAgents,
            getCallCustomers,
            showSettings,
            settingsPages.showUserManagement,
            orchestrationPages.showJourneys,
            showAIAgents,
            showReporting,
            showADToAgent,
            showADToAdmin,
            audiencePages.showContacts,
            orchestrationPages.showCampaigns,
        ]
    );

    const handleSearch = useCallback(
        (value: string) => {
            debouncedSearch(value);
        },
        [debouncedSearch]
    );

    useEffect(() => {
        if (isOpen) {
            // Show default results when opening
            setOptions(
                getDefaultResults(navigate, handleCloseModal, {
                    showAgentDesktop: showADToAgent || showADToAdmin,
                    showReporting,
                    showSettings,
                    showAIAgents,
                    showCustomAgents: aiAgentsPages.showCustomAgents,
                    showKnowledgeBase: aiAgentsPages.showKnowledgeBase,
                    showContacts: audiencePages.showContacts,
                    showCampaigns: orchestrationPages.showCampaigns,
                    showJourneys: orchestrationPages.showJourneys,
                    showUsers: showSettings && settingsPages.showUserManagement,
                })
            );
            setSelectedTabIndex(0);
            setSelectedItemIndex(0);
            setTimeout(() => {
                if (selectRef.current?.focus) {
                    selectRef.current.focus();
                }
                // Reset scroll position when reopening
                const container = containerRef.current;
                if (container) {
                    container.scrollTop = 0;
                }
            }, 100);
        } else {
            setSearchValue("");
            setSelectedTabIndex(0);
            setSelectedItemIndex(0);
        }
    }, [
        isOpen,
        navigate,
        handleCloseModal,
        showADToAgent,
        showADToAdmin,
        showReporting,
        showSettings,
        showAIAgents,
        aiAgentsPages.showCustomAgents,
        aiAgentsPages.showKnowledgeBase,
        audiencePages.showContacts,
        orchestrationPages.showCampaigns,
        orchestrationPages.showJourneys,
        settingsPages.showUserManagement,
    ]);

    useEffect(() => {
        if (isOpen) {
            setIsLoading(
                usersLoading ||
                    journeysLoading ||
                    campaignsLoading ||
                    aiAgentsLoading ||
                    callCustomersLoading ||
                    docsLoading ||
                    audiencesLoading
            );
        } else {
            setDocsLoading(false);
            setIsLoading(false);
        }
    }, [
        usersLoading,
        journeysLoading,
        campaignsLoading,
        aiAgentsLoading,
        callCustomersLoading,
        docsLoading,
        audiencesLoading,
        isOpen,
    ]);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        if (selectedItemIndex >= 0 && itemRefs.current[selectedItemIndex]) {
            itemRefs.current[selectedItemIndex]?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [selectedItemIndex, selectedTabIndex]);

    const tabs = useMemo(() => {
        const allTabs = [
            {
                label: "All",
                icon: <SearchOutlined />,
                children: options.flatMap((o) => o.options.map((o1) => o1.label)),
            },
            {
                label: "Routes",
                icon: <UnorderedListOutlined />,
                children: options.flatMap((o) => {
                    if (o.label === "Routing") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            },
        ];

        if (audiencePages.showContacts) {
            allTabs.push({
                label: "Audience",
                icon: <TeamOutlined />,
                children: options.flatMap((o) => {
                    if (o.label === "Audience") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            });
        }

        if (orchestrationPages.showJourneys) {
            allTabs.push({
                label: "Journeys",
                icon: <RVIcon icon={"journey"} className={styles.tabIcon} size={RVIconSizeOptions.XSMALL} />,
                children: options.flatMap((o) => {
                    if (o.label === "Journeys") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            });
        }

        if (orchestrationPages.showCampaigns) {
            allTabs.push({
                label: "Campaigns",
                icon: <RVIcon icon={"campaign"} className={styles.tabIcon} size={RVIconSizeOptions.SMALL} />,
                children: options.flatMap((o) => {
                    if (o.label === "Campaigns") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            });
        }

        if (showAIAgents) {
            allTabs.push({
                label: "AI Agents",
                icon: <RVIcon icon={"ai-agents"} className={styles.tabIcon} size={RVIconSizeOptions.SMALL} />,
                children: options.flatMap((o) => {
                    if (o.label === "AI Agents") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            });
        }

        if (showSettings && settingsPages.showUserManagement) {
            allTabs.push({
                label: "Users",
                icon: <UserOutlined />,
                children: options.flatMap((o) => {
                    if (o.label === "Users") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            });
        }

        allTabs.push(
            {
                label: "Regal Docs",
                icon: <FileSearchOutlined />,
                children: options.flatMap((o) => {
                    if (o.label === "Regal Docs") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            },
            {
                label: "Commands",
                icon: <FileSearchOutlined />,
                children: options.flatMap((o) => {
                    if (o.label === "Commands" || o.label === "Customers") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            },
            {
                label: "Audiences",
                icon: <TeamOutlined />,
                children: options.flatMap((o) => {
                    if (o.label === "Audiences") {
                        return o.options.map((o1) => o1.label);
                    }
                    return [];
                }),
            }
        );
        // Filter tabs to only show "All" and tabs that have children
        const hasOnlyCommands = allTabs.every(
            (tab) => tab.label === "All" || tab.label === "Commands" || tab.children.length === 0
        );

        const hasCustomers = options.some((o) => o.label === "Customers");
        const isCallCommand = searchValue.startsWith("/call ");

        return allTabs
            .filter((tab) => {
                if (hasOnlyCommands || isCallCommand) {
                    return tab.label === "Commands" && (tab.children.length > 0 || isCallCommand);
                }
                return tab.label === "All" || tab.children.length > 0;
            })
            .map((tab, i) => ({
                key: i.toString(),
                label: (
                    <span>
                        {tab.icon}{" "}
                        {(hasCustomers || isCallCommand) && tab.label === "Commands" ? "Customers" : tab.label}
                    </span>
                ),
                children: tab.children ?? <span>Tab</span>,
            }));
    }, [
        options,
        searchValue,
        showAIAgents,
        showSettings,
        settingsPages.showUserManagement,
        audiencePages.showContacts,
        orchestrationPages.showCampaigns,
        orchestrationPages.showJourneys,
    ]);

    const currentTabItems = useMemo(() => {
        if (!tabs[selectedTabIndex]) {
            return [];
        }
        if (selectedTabIndex === 0) {
            return options.flatMap((option) => option.options.map((opt) => opt.label));
        }
        return Array.isArray(tabs[selectedTabIndex].children) ? tabs[selectedTabIndex].children : [];
    }, [tabs, selectedTabIndex, options]);

    const ensureItemInView = useCallback((index: number) => {
        requestAnimationFrame(() => {
            const container = containerRef.current;
            const item = itemRefs.current[index];
            if (!container || !item) {
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const itemRect = item.getBoundingClientRect();

            // Calculate the scroll position needed to make the item visible
            if (itemRect.top < containerRect.top) {
                // Item is above the visible area
                container.scrollTop = item.offsetTop - container.offsetTop;
            } else if (itemRect.bottom > containerRect.bottom) {
                // Item is below the visible area
                container.scrollTop = item.offsetTop - container.offsetTop - (containerRect.height - itemRect.height);
            }
        });
    }, []);

    const focusInput = useCallback(() => {
        requestAnimationFrame(() => {
            const input = selectRef.current?.input?.input;
            if (input) {
                input.focus();
                const length = input.value.length;
                input.setSelectionRange(length, length);
            }
        });
    }, []);

    const openRouteInNewTab = useCallback((route: string) => {
        const href = new URL(routerBaseName + route, window.location.origin).href;
        window.open(href, "_blank", "noopener,noreferrer");
    }, []);

    const handleSpotlightModifierClick = useCallback(
        (item: SpotlightItem, event: React.MouseEvent<HTMLElement>) => {
            if (!item.props.route || (!event.metaKey && !event.ctrlKey)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            openRouteInNewTab(item.props.route);
        },
        [openRouteInNewTab]
    );

    const enhancedTabs = useMemo(() => {
        return tabs.map((tab, tabIndex) => ({
            ...tab,
            children: Array.isArray(tab.children) ? (
                <div
                    className={styles.tabContent}
                    ref={containerRef}
                    key={tabIndex}
                    id={`spotlight-tab-content-${tabIndex}`}
                >
                    {tabIndex === 0 // "All" tab
                        ? options.flatMap((option, optionIndex) =>
                              option.options.map((opt, optIndex) => {
                                  const flatIndex =
                                      options
                                          .slice(0, optionIndex)
                                          .reduce((acc, curr) => acc + curr.options.length, 0) + optIndex;
                                  return (
                                      <div
                                          ref={(el) => {
                                              if (el) {
                                                  itemRefs.current[flatIndex] = el;
                                              }
                                          }}
                                          key={`${optionIndex}-${optIndex}`}
                                          className={`${opt.disabled ? styles.disabled : styles.searchItem} ${
                                              tabIndex === selectedTabIndex && flatIndex === selectedItemIndex
                                                  ? styles.selected
                                                  : ""
                                          }`}
                                          onMouseEnter={() => {
                                              setSelectedItemIndex(flatIndex);
                                              ensureItemInView(flatIndex);
                                          }}
                                          onClickCapture={(event) => {
                                              if (isValidElement(opt.label) && "onCallback" in opt.label.props) {
                                                  handleSpotlightModifierClick(opt.label as SpotlightItem, event);
                                              }
                                          }}
                                          onClick={() => {
                                              if (isValidElement(opt.label) && "onCallback" in opt.label.props) {
                                                  (opt.label as SpotlightItem).props.onCallback?.();
                                                  handleCloseModal();
                                              }
                                          }}
                                      >
                                          {opt.label}
                                      </div>
                                  );
                              })
                          )
                        : tab.children.map((child, itemIndex) => (
                              <div
                                  ref={(el) => {
                                      if (el) {
                                          itemRefs.current[itemIndex] = el;
                                      }
                                  }}
                                  key={itemIndex}
                                  className={`${styles.searchItem} ${
                                      tabIndex === selectedTabIndex && itemIndex === selectedItemIndex
                                          ? styles.selected
                                          : ""
                                  }`}
                                  onMouseEnter={() => {
                                      setSelectedItemIndex(itemIndex);
                                      ensureItemInView(itemIndex);
                                  }}
                                  onClickCapture={(event) => {
                                      if (isValidElement(child) && "onCallback" in child.props) {
                                          handleSpotlightModifierClick(child as SpotlightItem, event);
                                      }
                                  }}
                                  onClick={() => {
                                      if (isValidElement(child) && "onCallback" in child.props) {
                                          (child as SpotlightItem).props.onCallback?.();
                                          handleCloseModal();
                                      }
                                  }}
                              >
                                  {child}
                              </div>
                          ))}
                </div>
            ) : (
                tab.children
            ),
        }));
    }, [
        tabs,
        selectedTabIndex,
        selectedItemIndex,
        handleCloseModal,
        options,
        ensureItemInView,
        handleSpotlightModifierClick,
    ]);

    return (
        <RVModal
            size="medium"
            open={isOpen}
            onCancel={onCancel}
            footer={null}
            closeIcon={null}
            className={styles.spotlightModal}
            destroyOnClose
        >
            <RVColumn gap={2}>
                <RVColumn gap={2}>
                    <Select
                        open={false}
                        ref={selectRef}
                        autoFocus
                        showSearch
                        options={options}
                        placeholder="Search for pages, journeys, users, AI agents and more..."
                        onSearch={handleSearch}
                        filterOption={false}
                        notFoundContent={null}
                        className={styles.searchSelect}
                        suffixIcon={<SearchOutlined />}
                        value={null}
                        onFocus={() => focusInput()}
                        onClick={() => setIsKeyboardNavigation(false)}
                        onBlur={(e) => {
                            if (!e.relatedTarget?.closest(".ant-select")) {
                                focusInput();
                            }
                        }}
                        onInputKeyDown={(e) => {
                            if (e.key === "Enter") {
                                // For command shortcuts
                                if (
                                    commands.some((option) => option.activateOnEnter && option.action === searchValue)
                                ) {
                                    const findAction = commands.find((option) => option.action === searchValue);
                                    if (findAction) {
                                        findAction.function?.();
                                        handleCloseModal();
                                        return;
                                    }
                                }
                                return;
                            }
                        }}
                        onKeyDown={(e) => {
                            // Allow text selection and cursor movement
                            if (e.metaKey || e.shiftKey || e.ctrlKey || e.altKey) {
                                return;
                            }

                            // Handle navigation keys
                            if (
                                isKeyboardNavigation &&
                                ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key)
                            ) {
                                e.preventDefault();
                                switch (e.key) {
                                    case "ArrowUp":
                                        if (currentTabItems.length > 0) {
                                            setSelectedItemIndex((prev) => {
                                                const newIndex = prev > 0 ? prev - 1 : currentTabItems.length - 1;
                                                ensureItemInView(newIndex);
                                                return newIndex;
                                            });
                                        }
                                        break;
                                    case "ArrowDown":
                                        if (currentTabItems.length > 0) {
                                            setSelectedItemIndex((prev) => {
                                                const newIndex = prev < currentTabItems.length - 1 ? prev + 1 : 0;
                                                ensureItemInView(newIndex);
                                                return newIndex;
                                            });
                                        }
                                        break;
                                    case "ArrowLeft":
                                        if (tabs.length > 0) {
                                            setSelectedTabIndex((prev) => {
                                                const newIndex = prev > 0 ? prev - 1 : tabs.length - 1;
                                                setSelectedItemIndex(0);
                                                return newIndex;
                                            });
                                        }
                                        break;
                                    case "ArrowRight":
                                        if (tabs.length > 0) {
                                            setSelectedTabIndex((prev) => {
                                                const newIndex = prev < tabs.length - 1 ? prev + 1 : 0;
                                                setSelectedItemIndex(0);
                                                return newIndex;
                                            });
                                        }
                                        break;
                                    case "Enter":
                                        if (selectedItemIndex === -1 || !currentTabItems[selectedItemIndex]) {
                                            return;
                                        }

                                        // Get the current tab's items
                                        const currentTab = tabs[selectedTabIndex];
                                        if (!currentTab || !Array.isArray(currentTab.children)) {
                                            return;
                                        }

                                        // Find the selected item in the current tab
                                        const selectedItem = currentTab.children[selectedItemIndex];
                                        if (isValidElement(selectedItem) && "onCallback" in selectedItem.props) {
                                            (selectedItem as SpotlightItem).props.onCallback?.();
                                            handleCloseModal();
                                        }
                                        break;
                                }
                            }
                        }}
                        onSelect={(value: any, option: any) => {
                            if (commands.some((option) => option.action === value.split(" ")[0])) {
                                const findAction = commands.find((option) => option.action === value.split(" ")[0]);
                                if (findAction) {
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    const [_, ...values] = value.split(" ").filter((value: any) => value !== "");
                                    findAction.function?.(values);
                                }
                                handleCloseModal();
                                setSearchValue(value);
                                return;
                            }
                            if (typeof option?.label?.props?.onCallback === "function") {
                                option.label.props.onCallback();
                                handleCloseModal();
                            }
                        }}
                    />
                </RVColumn>
                {(searchValue.length > 0 || options.length > 0) && (!isLoading || searchValue.startsWith("/")) && (
                    <Tabs
                        destroyInactiveTabPane
                        items={enhancedTabs.filter((tab) => tab.children.props?.children?.length > 0)}
                        activeKey={selectedTabIndex.toString()}
                        onChange={(key) => {
                            const newTabIndex = Number(key);
                            setSelectedTabIndex(newTabIndex);

                            // Force scroll reset with a more direct approach
                            setTimeout(() => {
                                // Try multiple approaches to ensure scrolling works
                                const container = document.getElementById(`spotlight-tab-content-${newTabIndex}`);
                                if (container) {
                                    container.scrollTop = 0;
                                }

                                if (containerRef.current) {
                                    containerRef.current.scrollTop = 0;
                                }

                                // Reset selected item index after scroll reset
                                setSelectedItemIndex(0);

                                // Ensure focus remains on input after changing tabs
                                selectRef.current?.focus();
                            }, 100);
                        }}
                    />
                )}
                {searchValue.length > 0 && !isLoading && !options?.length && (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            searchValue.startsWith("/call ")
                                ? "Start typing to search a customer"
                                : searchValue
                                  ? "No results match the criteria"
                                  : "Start typing to search…"
                        }
                    />
                )}
                {isLoading && <Spin />}
            </RVColumn>
        </RVModal>
    );
}
